// ═══════════════════════════════════════════════════════════
// AuthContext — авторизація та права доступу
// Firebase Auth + Firestore users, 27+ гранулярних permissions
// Ролі: admin, manager, master (з LEGACY_MAP для сумісності)
// Multi-tenant: user_tenants/{uid} → tenantId → tenant-scoped data
// ═══════════════════════════════════════════════════════════
import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, getDocs, collection } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { setImpersonatedTenant } from '../services/api';
import { identify, resetAnalytics } from '../services/analytics';
import { hasPermission as _hasPermission } from '../utils/permissions';

const AuthContext = createContext(null);

/* ── Role presets ── */
const ALL_PERMISSIONS = {
  // Clients
  clients_view: true, clients_edit: true, clients_delete: true, clients_categories: true, clients_documents: true,
  documents_templates: true,
  // Staff
  staff_view: true, staff_edit: true, schedule_view: true, schedule_edit: true, services_view: true, services_edit: true,
  // Calendar
  calendar_view: true, calendar_create: true, calendar_edit: true,
  // POS
  pos_sell: true,
  // Orders
  orders_view: true, orders_create: true, orders_edit: true,
  // Inventory
  inventory_view: true, inventory_purchases: true, inventory_movements: true, production_view: true, production_create: true,
  // Loyalty
  loyalty_view: true, loyalty_edit: true,
  // Salary
  salary_view: true, salary_edit: true,
  // Finances & analytics
  finances_view: true, cash_register: true, reports_view: true, financial_result: true, business_monitor: true,
  // Communications
  communication_view: true, comm_settings_edit: true,
  // Settings
  settings_locations: true, settings_booking: true, settings_integrations: true, settings_tariffs: true,
  // Tasks
  tasks_view: true, tasks_view_all: true, tasks_create: true, tasks_assign: true,
  tasks_edit_all: true, tasks_manage: true, tasks_reports: true,
  projects_manage: true,
  // System
  manage_users: true, manage_roles: true, audit_log_view: true,
};

// MANAGER_PERMISSIONS, MASTER_PERMISSIONS and DEFAULT_ROLES used to live
// here. Nothing imported them: tenants are seeded server-side from
// SYSTEM_ROLE_PRESETS in api/_lib/permissions.js. A dead copy that looks
// like a source of truth is worse than no copy, so it is gone.

export { ALL_PERMISSIONS };

/**
 * Get the base path for tenant-scoped collections.
 * If tenantId exists: tenants/{tenantId}/{collection}
 * Otherwise: top-level {collection} (legacy/fallback)
 */
function tenantPath(tenantId, ...segments) {
  if (tenantId) return ['tenants', tenantId, ...segments].join('/');
  return segments.join('/');
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);   // Firebase user
  const [userProfile, setUserProfile] = useState(null);    // Firestore user doc
  const [role, setRole] = useState(null);                  // role doc with permissions
  const [tenantId, setTenantId] = useState(null);          // tenant ID
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [impersonatedTenantId, setImpersonatedTenantIdState] = useState(
    () => sessionStorage.getItem('impersonatedTenantId') || null
  );
  const [availableTenants, setAvailableTenants] = useState([]);

  /* Auth state listener */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // 1. Resolve tenantId from user_tenants/{uid}
        let resolvedTenantId = null;
        try {
          const tenantSnap = await getDoc(doc(db, 'user_tenants', user.uid));
          if (tenantSnap.exists()) {
            resolvedTenantId = tenantSnap.data()?.tenantId || null;
          }
        } catch (_) { /* no mapping yet */ }
        setTenantId(resolvedTenantId);

        // Check superadmin status + load tenant list
        try {
          const saSnap = await getDoc(doc(db, 'superadmins', user.uid));
          const sa = saSnap.exists();
          setIsSuperAdmin(sa);
          if (sa) {
            const tenantsSnap = await getDocs(collection(db, 'tenants'));
            setAvailableTenants(tenantsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            // Restore impersonated tenant from sessionStorage
            const savedTid = sessionStorage.getItem('impersonatedTenantId');
            if (savedTid) {
              setImpersonatedTenantIdState(savedTid);
              setImpersonatedTenant(savedTid);
            }
          } else {
            // Not a superadmin — clear any stale session
            sessionStorage.removeItem('impersonatedTenantId');
          }
        } catch { setIsSuperAdmin(false); }

        // 2. Load user profile (tenant-scoped or legacy top-level)
        const usersPath = tenantPath(resolvedTenantId, 'users');
        let profileSnap = await getDoc(doc(db, usersPath, user.uid));

        // Fallback: try top-level if tenant-scoped not found
        if (!profileSnap.exists() && resolvedTenantId) {
          profileSnap = await getDoc(doc(db, 'users', user.uid));
        }

        if (profileSnap.exists()) {
          const profile = { id: profileSnap.id, ...profileSnap.data() };
          setUserProfile(profile);

          // 3. Load role (tenant-scoped or legacy top-level)
          let resolvedRoleId = profile.roleId || null;
          if (profile.roleId === 'admin') {
            // The admin role means the whole catalogue, resolved here rather
            // than read from storage — mirroring what verifyAuth does on the
            // server, so both sides answer "may this person do X?" the same
            // way.
            //
            // DEFAULT_ROLES seeds admin with every permission, but only when
            // the tenant registers; a key added later never reaches roles
            // that already exist. Reading the stored map meant the sidebar
            // hid the Tasks entry from admins the day its permissions were
            // introduced, while the API was letting them through — and
            // earlier, three of six tenants had an admin missing 18 of 41.
            //
            // Only admin gets this. For a partial role a missing key is
            // ambiguous — nobody can tell whether it was withheld or simply
            // did not exist yet — and the safe reading is "not granted",
            // since an admin can hand it over with one checkbox. Admin has
            // nobody above it to do that, so it has to be self-healing:
            // an admin who somehow lost manage_roles could not even open the
            // Roles page to repair themselves.
            setRole({ id: 'admin', name: 'Адміністратор', isSystem: true, permissions: ALL_PERMISSIONS });
          } else if (profile.roleId) {
            const rolesPath = tenantPath(resolvedTenantId, 'roles');
            let roleSnap = await getDoc(doc(db, rolesPath, profile.roleId));
            if (!roleSnap.exists() && resolvedTenantId) {
              roleSnap = await getDoc(doc(db, 'roles', profile.roleId));
            }
            if (roleSnap.exists()) {
              setRole({ id: roleSnap.id, ...roleSnap.data() });
            }
          }

          // Bind analytics session to the user (anon → identified)
          identify(user.uid, {
            email: user.email,
            tenantId: resolvedTenantId,
            roleId: resolvedRoleId,
          });
        }
      } else {
        setUserProfile(null);
        setRole(null);
        setTenantId(null);
        setIsSuperAdmin(false);
        setImpersonatedTenantIdState(null);
        setImpersonatedTenant(null);
        sessionStorage.removeItem('impersonatedTenantId');
        setAvailableTenants([]);
        resetAnalytics();
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Superadmin: switch to a different tenant (persists in sessionStorage)
  const setImpersonatedTenantId = (tid) => {
    setImpersonatedTenantIdState(tid);
    setImpersonatedTenant(tid); // sync to api.js header
    if (tid) {
      sessionStorage.setItem('impersonatedTenantId', tid);
    } else {
      sessionStorage.removeItem('impersonatedTenantId');
    }
  };

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const logout = () => signOut(auth);

  const hasPermission = (key) => _hasPermission(role, key);

  const changePassword = async (currentPassword, newPassword) => {
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    await reauthenticateWithCredential(currentUser, credential);
    await updatePassword(currentUser, newPassword);
  };

  const updateDisplayName = async (newName) => {
    const usersPath = tenantPath(tenantId, 'users');
    await setDoc(doc(db, usersPath, currentUser.uid), { displayName: newName }, { merge: true });
    setUserProfile(prev => ({ ...prev, displayName: newName }));
  };

  const updateHomePage = async (page) => {
    const usersPath = tenantPath(tenantId, 'users');
    await setDoc(doc(db, usersPath, currentUser.uid), { homePage: page }, { merge: true });
    setUserProfile(prev => ({ ...prev, homePage: page }));
  };

  const updateStartingLocation = async (locationId) => {
    // Per-user preferred default location id. Stored as string to match
    // how locationId is compared everywhere else in the app.
    const usersPath = tenantPath(tenantId, 'users');
    const value = locationId ? String(locationId) : null;
    await setDoc(doc(db, usersPath, currentUser.uid), { startingLocationId: value }, { merge: true });
    setUserProfile(prev => ({ ...prev, startingLocationId: value }));
  };

  const value = {
    currentUser,
    userProfile,
    role,
    tenantId,
    loading,
    isSuperAdmin,
    impersonatedTenantId,
    setImpersonatedTenantId,
    availableTenants,
    login,
    logout,
    resetPassword,
    hasPermission,
    changePassword,
    updateDisplayName,
    updateHomePage,
    updateStartingLocation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
